import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clientApi, ApiError, clientErrorMessage, type SuggestionLock } from './api';
import { showToast } from '../components/Toaster';
import type { PublicSuggestion } from '../../shared/types';
import { getOrCreateLockToken } from './lockToken';
import { getStoredClientName } from './clientName';
import { publicLockState } from '../../shared/suggestionLock';
import { createLatestSaveQueue, type LatestSaveOutcome } from './latestSaveQueue';

const POLL_INTERVAL = 15_000;
const AUTOSAVE_MS = 1500;

export type SuggestSaveState = 'clean' | 'dirty' | 'saving' | 'saved' | 'error';

export interface SuggestionLockState {
  /** Someone else holds a fresh lock — I'm read-only until they step away. */
  lockedByOther: boolean;
  /** Display name of the current/last lock holder ('' if unknown). */
  holderName: string;
  /** A former holder's lock has gone stale — I may take over now. */
  staleTakeoverAvailable: boolean;
  /** I was editing but just lost the lock (someone else took over). */
  lockLost: boolean;
}

export interface AcquireResult {
  ok: boolean;
  holderName: string;
  /** Why it failed, when ok is false — lets the caller show accurate copy
   *  instead of always assuming a teammate's lock is the cause. Undefined
   *  when ok is true. */
  reason?: 'locked' | 'approved' | 'other';
}

/** Classify a failed start/resume by HTTP status, so the caller can tell a
 *  teammate's lock (409) apart from the script having been approved out
 *  from under this request (403 — see the client.ts approved-status gate). */
function reasonFrom(err: unknown): 'locked' | 'approved' | 'other' {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'locked';
    if (err.status === 403) return 'approved';
  }
  return 'other';
}

export interface UseClientSuggestionResult {
  suggestion: PublicSuggestion | null;
  loading: boolean;
  saveState: SuggestSaveState;
  /** Back-compat alias: a teammate holds a fresh lock. */
  teammateEditing: boolean;
  lock: SuggestionLockState;
  start: () => Promise<AcquireResult>;
  resume: () => Promise<AcquireResult>;
  scheduleAutosave: (doc_json: string) => void;
  flush: () => Promise<boolean>;
  /** The draft edit_version this tab last saved or saw, current even right after `flush()`. */
  latestEditVersion: () => number;
  /**
   * Whether the document on screen is the one the server holds: nothing is
   * queued for saving and the last save did not fail. Read it right after
   * `flush()`; a comment anchor must not be recorded against unsaved text.
   */
  draftIsSaved: () => boolean;
  retrySave: () => Promise<void>;
  submit: (params: { author_name: string; note: string }) => Promise<void>;
  refresh: () => Promise<void>;
  heartbeat: () => Promise<void>;
}

const NO_LOCK: SuggestionLockState = {
  lockedByOther: false,
  holderName: '',
  staleTakeoverAvailable: false,
  lockLost: false,
};

function holderFrom(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const h = (err.body as { holder_name?: unknown }).holder_name;
    if (typeof h === 'string') return h;
  }
  return '';
}

function errorCodeFrom(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return '';
}

export function useClientSuggestion(slug: string | undefined): UseClientSuggestionResult {
  const [suggestion, setSuggestion] = useState<PublicSuggestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<SuggestSaveState>('clean');
  // Mirror of saveState for callers that need the value straight after an
  // await, before React has re-rendered.
  const saveStateRef = useRef<SuggestSaveState>('clean');
  const updateSaveState = useCallback((next: SuggestSaveState) => {
    saveStateRef.current = next;
    setSaveState(next);
  }, []);
  const [lock, setLock] = useState<SuggestionLockState>(NO_LOCK);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionIdRef = useRef<string | null>(null);
  const suggestionVersionRef = useRef(1);
  const latestDocRef = useRef<string | null>(null);
  const saveAttemptRef = useRef<(doc: string) => Promise<LatestSaveOutcome>>(async () => 'retry');
  const queueRef = useRef(
    createLatestSaveQueue((doc: string) => saveAttemptRef.current(doc)),
  );

  const token = useMemo(() => getOrCreateLockToken(), []);
  const lockPayload = useCallback(
    (): SuggestionLock => ({ lock_token: token, author_name: getStoredClientName() }),
    [token],
  );

  // Derive the lock UI state from a freshly-fetched suggestion.
  const applyLockState = useCallback(
    (s: PublicSuggestion | null) => {
      if (!s || s.status !== 'editing') {
        setLock(NO_LOCK);
        return;
      }
      const { iHold, byOther, staleForeign } = publicLockState(s, Date.now());
      setLock((prev) => ({
        lockedByOther: byOther,
        holderName: s.lock_holder_name || (byOther || staleForeign ? prev.holderName : ''),
        staleTakeoverAvailable: staleForeign,
        // Clear the "lost the lock" flag once I hold it again.
        lockLost: prev.lockLost && !iHold,
      }));
    },
    [],
  );

  const rememberSuggestion = useCallback((s: PublicSuggestion | null) => {
    if (!s) {
      suggestionIdRef.current = null;
      latestDocRef.current = null;
      return;
    }
    if (
      suggestionIdRef.current !== s.id ||
      s.edit_version >= suggestionVersionRef.current
    ) {
      suggestionIdRef.current = s.id;
      suggestionVersionRef.current = s.edit_version;
      latestDocRef.current = s.doc_json;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await clientApi.getActiveSuggestion(slug, token);
      rememberSuggestion(res.suggestion);
      setSuggestion(res.suggestion);
      applyLockState(res.suggestion);
    } catch {
      // soft-fail
    } finally {
      setLoading(false);
    }
  }, [slug, token, applyLockState, rememberSuggestion]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [refresh]);

  const start = useCallback(async (): Promise<AcquireResult> => {
    if (!slug) return { ok: false, holderName: '', reason: 'other' };
    try {
      const res = await clientApi.startSuggestion(slug, lockPayload());
      rememberSuggestion(res.suggestion);
      setSuggestion(res.suggestion);
      applyLockState(res.suggestion);
      setLock((p) => ({ ...p, lockLost: false }));
      return { ok: true, holderName: getStoredClientName() };
    } catch (err) {
      const holder = holderFrom(err);
      const reason = reasonFrom(err);
      if (reason === 'locked') {
        setLock({ ...NO_LOCK, lockedByOther: true, holderName: holder });
        void refresh();
      } else if (reason === 'approved') {
        // The script was approved out from under this request — refresh so
        // suggestion/script state (and the page around it) catch up.
        void refresh();
      }
      return { ok: false, holderName: holder, reason };
    }
  }, [slug, lockPayload, applyLockState, refresh, rememberSuggestion]);

  const resume = useCallback(async (): Promise<AcquireResult> => {
    if (!slug) return { ok: false, holderName: '', reason: 'other' };
    try {
      await clientApi.resumeSuggestion(slug, lockPayload());
      setLock((p) => ({ ...p, lockLost: false }));
      await refresh();
      return { ok: true, holderName: getStoredClientName() };
    } catch (err) {
      const holder = holderFrom(err);
      const reason = reasonFrom(err);
      if (reason === 'locked') {
        setLock({ ...NO_LOCK, lockedByOther: true, holderName: holder });
        void refresh();
      } else if (reason === 'approved') {
        void refresh();
      }
      return { ok: false, holderName: holder, reason };
    }
  }, [slug, lockPayload, refresh]);

  saveAttemptRef.current = async (doc: string): Promise<LatestSaveOutcome> => {
    if (!slug) return 'retry';
    const expectedVersion = suggestionVersionRef.current;
    try {
      const result = await clientApi.updateSuggestion(
        slug,
        doc,
        expectedVersion,
        lockPayload(),
      );
      suggestionVersionRef.current = result.edit_version;
      latestDocRef.current = doc;
      setSuggestion((prev) =>
        prev
          ? { ...prev, doc_json: doc, edit_version: result.edit_version, updated_at: Date.now() }
          : prev,
      );
      setLock((p) => (p.lockLost ? { ...p, lockLost: false } : p));
      return 'saved';
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        queueRef.current.clear();
        if (timerRef.current) clearTimeout(timerRef.current);
        updateSaveState('clean');
        rememberSuggestion(null);
        setSuggestion(null);
        applyLockState(null);
        return 'discard';
      }
      if (err instanceof ApiError && err.status === 413) {
        // Retrying the same document can only 413 again, and the queue would
        // otherwise keep it pending. Say why, and leave the indicator on
        // error so the client knows the last change did not land.
        showToast('error', clientErrorMessage(err, 'That change is too large to save.'));
        updateSaveState('error');
        return 'discard';
      }
      if (err instanceof ApiError && err.status === 409 && errorCodeFrom(err) === 'locked') {
        const holder = holderFrom(err);
        setLock((p) => ({
          ...p,
          lockLost: true,
          lockedByOther: true,
          holderName: holder || p.holderName,
        }));
      }
      updateSaveState('error');
      return 'retry';
    }
  };

  const flush = useCallback(async (): Promise<boolean> => {
    if (!slug) return false;
    if (!queueRef.current.hasWork()) return true;
    updateSaveState('saving');
    const saved = await queueRef.current.flush();
    if (saved) updateSaveState('saved');
    return saved;
  }, [slug]);

  const retrySave = useCallback(async () => {
    await flush();
  }, [flush]);

  const scheduleAutosave = useCallback(
    (doc_json: string) => {
      latestDocRef.current = doc_json;
      queueRef.current.schedule(doc_json);
      updateSaveState('dirty');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => void flush(), AUTOSAVE_MS);
    },
    [flush],
  );

  const heartbeat = useCallback(async () => {
    if (!slug) return;
    try {
      await clientApi.suggestionHeartbeat(slug, lockPayload());
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const holder = holderFrom(err);
        setLock((p) => ({
          ...p,
          lockLost: true,
          lockedByOther: true,
          holderName: holder || p.holderName,
        }));
      }
    }
  }, [slug, lockPayload]);

  const submit = useCallback(
    async (params: { author_name: string; note: string }) => {
      if (!slug) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      const saved = await flush();
      if (!saved || latestDocRef.current === null) {
        throw new Error('The latest suggestion changes could not be saved.');
      }
      try {
        const result = await clientApi.submitSuggestion(slug, {
          ...params,
          doc_json: latestDocRef.current,
          lock_token: token,
          expected_version: suggestionVersionRef.current,
        });
        suggestionVersionRef.current = result.edit_version;
        queueRef.current.clear();
        await refresh();
      } catch (err) {
        // Both 409s are dead ends without an explanation: a second viewer
        // pressing "Send for review" doesn't hold the lock, and a stale tab
        // is submitting a version someone else has already moved past.
        // Translate each into the action that actually resolves it.
        const code = errorCodeFrom(err);
        if (code === 'locked') {
          const holder = holderFrom(err);
          setLock((p) => ({
            ...p,
            lockLost: true,
            lockedByOther: true,
            holderName: holder || p.holderName,
          }));
          throw new Error(
            `${holder || 'Someone else'} is editing this draft right now, so it can't be sent from here. ` +
              'Ask them to send it, or take over editing and send it yourself.',
          );
        }
        if (code === 'stale') {
          await refresh();
          throw new Error(
            'This draft was changed somewhere else while you were sending it. ' +
              'We’ve loaded the latest version — take a look, then send again.',
          );
        }
        throw err;
      }
    },
    [slug, token, flush, refresh],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    suggestion,
    loading,
    saveState,
    teammateEditing: lock.lockedByOther,
    lock,
    start,
    resume,
    scheduleAutosave,
    flush,
    latestEditVersion: () => suggestionVersionRef.current,
    draftIsSaved: () => !queueRef.current.hasWork() && saveStateRef.current !== 'error',
    retrySave,
    submit,
    refresh,
    heartbeat,
  };
}
